import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Users,
  BarChart3,
  CheckCircle,
  Square,
  CheckSquare,
  Calendar,
  Tag,
  BookOpen,
  Loader,
  AlertCircle,
} from "lucide-react";
import { apiService } from "../../services/api";
import "./AssessmentDetails.css";

const AssessmentDetails = () => {
  const { assessmentId } = useParams();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAssessment = async () => {
      try {
        setLoading(true);
        setError(null);

        const response =
          await apiService.assessments.getAssessment(assessmentId);

        if (response && !response.error) {
          setAssessment(response);
        } else {
          throw new Error(response?.error || "Assessment not found");
        }
      } catch (err) {
        console.error("Error fetching assessment:", err);
        setError(err.message || "Failed to load assessment");
      } finally {
        setLoading(false);
      }
    };

    if (assessmentId) {
      fetchAssessment();
    }
  }, [assessmentId]);

  if (loading) {
    return (
      <div className="assessment-details-page">
        <div className="loading-state">
          <Loader size={48} className="spinner" />
          <h3>Loading assessment details...</h3>
          <p>Please wait while we fetch the assessment information</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="assessment-details-page">
        <div className="error-state">
          <AlertCircle size={48} />
          <h2>Failed to load assessment</h2>
          <p>{error}</p>
          <Link to="/admin/assessments" className="btn btn-primary">
            <ArrowLeft size={16} />
            Back to Assessments
          </Link>
        </div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="assessment-details-page">
        <div className="error-state">
          <AlertCircle size={48} />
          <h2>Assessment Not Found</h2>
          <p>
            The assessment you're looking for doesn't exist or has been deleted.
          </p>
          <Link to="/admin/assessments" className="btn btn-primary">
            <ArrowLeft size={16} />
            Back to Assessments
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="assessment-details-page">
      <div className="assessment-details-container">
        {/* Header */}
        <div className="details-header">
          <Link to="/admin/assessments" className="back-button">
            <ArrowLeft size={20} />
            Back to Assessments
          </Link>

          <div className="header-content">
            <div className="header-info">
              <h1>{assessment.title}</h1>
              <div className="assessment-meta">
                <span className="meta-item">
                  <Calendar size={16} />
                  Created: {new Date(assessment.createdAt).toLocaleDateString()}
                </span>
                {/* <span className="meta-item">
                  <User size={16} />
                  By: {assessment.creatorName || "Unknown"}
                </span> */}
                <span className="meta-item">
                  <Tag size={16} />
                  {assessment.skillName || "Unknown Skill"}
                </span>
              </div>
            </div>

            <div className="header-actions">
              {/* <button className="btn btn-outline">
                <Edit size={16} />
                Edit Assessment
              </button> */}
              {/* <button
                className="btn btn-danger"
                onClick={deleteAssessment}
                disabled={deleting}
              >
                <Trash2 size={16} />
                {deleting ? "Deleting..." : "Delete"}
              </button> */}
            </div>
          </div>
        </div>

        {/* Assessment Info Cards */}
        <div className="assessment-info-grid">
          <div className="info-card">
            <div className="card-icon">
              <Users size={24} />
            </div>
            <div className="card-content">
              <div className="card-number">0</div>
              <div className="card-label">Completions</div>
            </div>
          </div>

          <div className="info-card">
            <div className="card-icon">
              <BarChart3 size={24} />
            </div>
            <div className="card-content">
              <div className="card-number">-</div>
              <div className="card-label">Average Score</div>
            </div>
          </div>

          <div className="info-card">
            <div className="card-icon">
              <CheckCircle size={24} />
            </div>
            <div className="card-content">
              <div className="card-number">
                {assessment.questions?.length || 0}
              </div>
              <div className="card-label">Questions</div>
            </div>
          </div>

          <div className="info-card">
            <div className="card-icon">
              <BookOpen size={24} />
            </div>
            <div className="card-content">
              <div className="card-label">{assessment.type || "Quiz"}</div>
              <div className="card-sublabel">
                {assessment.difficultyLevel || "Beginner"} Level
              </div>
            </div>
          </div>
        </div>

        {/* Assessment Details */}
        <div className="assessment-content">
          <div className="content-section">
            <h2>Assessment Information</h2>

            {/* Core Information Group */}
            <div className="info-group">
              <h3 className="group-title">Core Information</h3>
              <div className="info-cards-grid">
                <div className="info-card-item primary">
                  <div className="info-card-header">
                    <span className="info-card-label">Title</span>
                  </div>
                  <div
                    className="info-card-value title-value"
                    title={assessment.title}
                  >
                    {assessment.title}
                  </div>
                </div>

                <div className="info-card-item">
                  <div className="info-card-header">
                    <span className="info-card-label">Description</span>
                  </div>
                  <div
                    className="info-card-value description-value"
                    title={assessment.description || "No description provided"}
                  >
                    {assessment.description || "No description provided"}
                  </div>
                </div>
              </div>
            </div>

            {/* Assessment Settings Group */}
            <div className="info-group">
              <h3 className="group-title">Assessment Settings</h3>
              <div className="info-cards-grid">
                <div className="info-card-item">
                  <div className="info-card-header">
                    <span className="info-card-label">Skill</span>
                  </div>
                  <div className="info-card-value">
                    <span className="skill-badge">{assessment.skillName}</span>
                  </div>
                </div>

                <div className="info-card-item">
                  <div className="info-card-header">
                    <span className="info-card-label">Type</span>
                  </div>
                  <div className="info-card-value">
                    <span
                      className={`type-badge ${
                        assessment.type || "assignment"
                      }`}
                    >
                      {assessment.type || "assignment"}
                    </span>
                  </div>
                </div>

                <div className="info-card-item">
                  <div className="info-card-header">
                    <span className="info-card-label">Difficulty</span>
                  </div>
                  <div className="info-card-value">
                    <span
                      className={`difficulty-badge ${
                        assessment.difficultyLevel || "beginner"
                      }`}
                    >
                      {assessment.difficultyLevel || "beginner"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Scoring & Metrics Group */}
            <div className="info-group">
              <h3 className="group-title">Scoring & Metrics</h3>
              <div className="info-cards-grid metrics-grid">
                <div className="info-card-item metric">
                  <div className="info-card-header">
                    <span className="info-card-label">Max Score</span>
                  </div>
                  <div className="info-card-value metric-value">
                    {assessment.maxScore || 100}
                  </div>
                </div>

                <div className="info-card-item metric">
                  <div className="info-card-header">
                    <span className="info-card-label">Passing Score</span>
                  </div>
                  <div className="info-card-value metric-value">
                    {assessment.passingScore || 70}%
                  </div>
                </div>

                <div className="info-card-item metric">
                  <div className="info-card-header">
                    <span className="info-card-label">Time Limit</span>
                  </div>
                  <div className="info-card-value metric-value">
                    {assessment.timeLimit
                      ? `${Math.round(assessment.timeLimit / 60)} min`
                      : "130 min"}
                  </div>
                </div>
              </div>
            </div>

            {/* System Information Group */}
            <div className="info-group system-info">
              <h3 className="group-title">System Information</h3>
              <div className="info-cards-grid">
                <div className="info-card-item system">
                  <div className="info-card-header">
                    <span className="info-card-label">Assessment ID</span>
                  </div>
                  <div
                    className="info-card-value system-value"
                    title={assessment.id}
                  >
                    <code>{assessment.id}</code>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Questions Section */}
          <div className="content-section">
            <h2>Questions ({assessment.questions?.length || 0})</h2>
            {assessment.questions && assessment.questions.length > 0 ? (
              <div className="questions-list">
                {assessment.questions.map((question, index) => (
                  <div key={question.id} className="question-detail-card">
                    <div className="question-header">
                      <span className="question-number">Q{index + 1}</span>
                      <span className="question-type">
                        {question.questionType === "multiple_choice"
                          ? "Multiple Choice"
                          : question.questionType}
                      </span>
                      <span className="question-points">
                        {question.points} points
                      </span>
                    </div>

                    <div className="question-content">
                      <h4>{question.questionText}</h4>

                      {assessment.questions &&
                        assessment.questions.length > 0 && (
                          <div className="question-options">
                            {question.options.map((option) => (
                              <div
                                key={option.option_id}
                                className={`option-item ${
                                  option.is_correct ? "correct" : ""
                                }`}
                              >
                                {option.is_correct ? (
                                  <CheckSquare size={16} />
                                ) : (
                                  <Square size={16} />
                                )}
                                <span>{option.option_text}</span>
                                {option.is_correct && (
                                  <span className="correct-indicator">
                                    Correct Answer
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-questions">
                <p>No questions have been added to this assessment yet.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentDetails;
